import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Upload, Image as ImageIcon, X, Check, Loader2, FolderOpen } from 'lucide-react';
import { toast } from 'sonner';
import { adminFetch } from '../../services/adminFetch';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://api.mt-productions.co.uk';

/**
 * Image picker / uploader for admin forms.
 * - Drop-zone or click to upload (auto-compresses on backend)
 * - "Choose from library" button to pick a previously uploaded image
 * - Replaces all the manual URL-paste inputs across the admin
 *
 * Props:
 *   value      - current image URL (string) — also accepts local /paths/ that won't preview from R2
 *   onChange   - fn(newUrl) called when user uploads/selects an image
 *   folder     - subfolder in R2 site/ (e.g. "news", "timeline", "team", "posters", "cutarounds")
 *   label      - optional label shown above (defaults: "Image")
 *   aspect     - tailwind class for preview aspect ratio (default "aspect-video")
 *   testId     - data-testid prefix
 */
export default function ImagePicker({
  value,
  onChange,
  folder = 'general',
  label = 'Image',
  aspect = 'aspect-video',
  testId = 'image-picker',
}) {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const fileInputRef = useRef(null);

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Only image files are allowed');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('folder', folder);
      const token = localStorage.getItem('adminToken');
      const res = await adminFetch(`${API_URL}/api/admin/site-images/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Upload failed');
      onChange(data.url);
      toast.success(`Uploaded (${Math.round(data.size / 1024)} KB)`);
    } catch (err) {
      toast.error(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  }, [folder, onChange]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  return (
    <div data-testid={testId}>
      {label && <label className="block text-sm font-medium text-white/80 mb-2">{label}</label>}

      {value ? (
        <div className="relative inline-block">
          <img
            src={value}
            alt="Selected"
            className={`${aspect} max-w-full w-64 h-auto object-cover rounded-lg border border-white/10`}
            onError={(e) => { e.target.style.opacity = 0.3; }}
          />
          <div className="absolute top-2 right-2 flex gap-1">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              data-testid={`${testId}-replace`}
              className="bg-black/70 hover:bg-black text-white text-xs px-2 py-1 rounded inline-flex items-center gap-1"
            >
              <Upload className="w-3 h-3" /> Replace
            </button>
            <button
              type="button"
              onClick={() => onChange('')}
              data-testid={`${testId}-remove`}
              className="bg-red-500/80 hover:bg-red-500 text-white p-1 rounded"
              title="Remove image"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        </div>
      ) : (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => fileInputRef.current?.click()}
          data-testid={`${testId}-dropzone`}
          className={`cursor-pointer border-2 border-dashed rounded-lg p-6 text-center transition-all ${
            dragOver ? 'border-[#D4AF37] bg-[#D4AF37]/10' : 'border-white/20 hover:border-[#D4AF37]/50 bg-white/5'
          }`}
        >
          {uploading ? (
            <div className="text-white/70">
              <Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin" />
              <p className="text-sm">Uploading & compressing…</p>
            </div>
          ) : (
            <div className="text-white/60">
              <Upload className="w-7 h-7 mx-auto mb-2" />
              <p className="text-sm font-medium text-white/80">Drop image here or click to upload</p>
              <p className="text-xs mt-1 text-white/40">Auto-compresses to web-friendly size</p>
            </div>
          )}
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
        data-testid={`${testId}-input`}
      />

      <div className="mt-2 flex items-center gap-2 flex-wrap">
        {!value && (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            data-testid={`${testId}-upload-btn`}
            className="text-xs bg-white/10 hover:bg-white/20 text-white px-3 py-1.5 rounded inline-flex items-center gap-1.5"
          >
            <Upload className="w-3 h-3" /> Upload
          </button>
        )}
        <button
          type="button"
          onClick={() => setLibraryOpen(true)}
          data-testid={`${testId}-library-btn`}
          className="text-xs bg-white/10 hover:bg-white/20 text-white px-3 py-1.5 rounded inline-flex items-center gap-1.5"
        >
          <FolderOpen className="w-3 h-3" /> Choose from library
        </button>
      </div>

      {libraryOpen && (
        <ImageLibrary
          folder={folder}
          onClose={() => setLibraryOpen(false)}
          onSelect={(url) => { onChange(url); setLibraryOpen(false); }}
        />
      )}
    </div>
  );
}

function ImageLibrary({ folder, onClose, onSelect }) {
  const [data, setData] = useState({ folders: {}, total: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(folder);

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('adminToken');
        const res = await adminFetch(`${API_URL}/api/admin/site-images`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const d = await res.json();
        setData(d);
      } catch (e) {
        toast.error('Could not load image library');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const folders = Object.keys(data.folders || {});
  const currentImages = filter === '__all__'
    ? Object.values(data.folders || {}).flat()
    : (data.folders?.[filter] || []);

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-[#0a0a3a] border border-white/10 rounded-xl max-w-5xl w-full max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="image-library"
      >
        <div className="p-5 border-b border-white/10 flex items-center justify-between">
          <h3 className="text-white font-bold text-lg">Image Library ({data.total})</h3>
          <button onClick={onClose} className="text-white/60 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-4 border-b border-white/10 flex flex-wrap gap-2">
          <button
            onClick={() => setFilter('__all__')}
            className={`text-xs px-3 py-1 rounded ${filter === '__all__' ? 'bg-[#D4AF37] text-black' : 'bg-white/10 text-white hover:bg-white/20'}`}
          >
            All
          </button>
          {folders.map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`text-xs px-3 py-1 rounded capitalize ${filter === f ? 'bg-[#D4AF37] text-black' : 'bg-white/10 text-white hover:bg-white/20'}`}
            >
              {f} ({data.folders[f].length})
            </button>
          ))}
        </div>

        <div className="overflow-y-auto p-4 flex-1">
          {loading ? (
            <div className="text-center py-10 text-white/60"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>
          ) : currentImages.length === 0 ? (
            <div className="text-center py-10 text-white/40">
              <ImageIcon className="w-10 h-10 mx-auto mb-2" />
              <p>No images uploaded yet in this category.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {currentImages.map((img) => (
                <button
                  key={img.key}
                  onClick={() => onSelect(img.url)}
                  className="group relative aspect-square overflow-hidden rounded-lg border border-white/10 hover:border-[#D4AF37] transition-colors"
                  data-testid={`library-img-${img.filename}`}
                >
                  <img src={img.url} alt={img.filename} className="w-full h-full object-cover" loading="lazy" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                    <Check className="w-8 h-8 text-white opacity-0 group-hover:opacity-100" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
